/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  TooltipProps,
  Legend,
} from 'recharts';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Typography } from '@altermaind/web/ui';
import { NavigationForChart } from '../NavigationForChart';

type Metric = {
  id?: string;
  name?: string;
};

type Stats = {
  metric?: Metric;
  score?: number;
  beginningPercentage?: number;
  intermediatePercentage?: number;
  advancedPercentage?: number;
  total?: number;
};

type ChartDatum = {
  name: string;
  beginning: number;
  intermediate: number;
  advanced: number;
  total?: number;
};

type Props = {
  data: Stats[] | undefined;
  total?: number;
  defaultItemsSize?: number;
};

const colors = {
  beginning: '#d9d9d9',
  intermediate: '#9b87f5',
  advanced: '#3b82f6',
};

const CustomTooltip = ({
  active,
  payload,
  total,
}: TooltipProps<number, string> & { total?: number }) => {
  const t = useTranslations('charts.stacked');

  if (active && payload && payload.length) {
    const internalTotal = payload[0].payload.total || total;

    return (
      <Box
        style={{
          backgroundColor: '#fff',
          color: '#000',
          borderRadius: '8px',
          padding: '8px 12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          fontSize: '14px',
        }}
      >
        <Box mb='xxs'>
          {t('advanced')} <strong>{payload[0].payload.advanced || 0}%</strong>
          {internalTotal && (
            <>
              {' '}
              -{' '}
              {t('totalPeople', {
                total: Number((payload[0].payload.advanced / 100) * (internalTotal || 0)).toFixed(
                  0,
                ),
              })}
            </>
          )}
        </Box>
        <Box mb='xxs'>
          {t('intermediate')} <strong>{payload[0].payload.intermediate || 0}%</strong>
          {internalTotal && (
            <>
              {' '}
              -{' '}
              {t('totalPeople', {
                total: Number(
                  (payload[0].payload.intermediate / 100) * (internalTotal || 0),
                ).toFixed(0),
              })}
            </>
          )}
        </Box>
        <Box mb={internalTotal ? 'xxs' : undefined}>
          {t('beginning')} <strong>{payload[0].payload.beginning || 0}%</strong>
          {internalTotal && (
            <>
              {' '}
              -{' '}
              {t('totalPeople', {
                total: Number((payload[0].payload.beginning / 100) * (internalTotal || 0)).toFixed(
                  0,
                ),
              })}
            </>
          )}
        </Box>
        {internalTotal && <Box>{t('totalCompleted', { total: internalTotal })}</Box>}
      </Box>
    );
  }
  return null;
};

const CustomLegend = ({ payload, averageBeginning, averageIntermediate, averageAdvanced }: any) => {
  const showAvarage = (key: string) => {
    if (key === 'beginning') {
      return averageBeginning;
    }
    if (key === 'intermediate') {
      return averageIntermediate;
    }
    return averageAdvanced;
  };

  return (
    <Box mt='l'>
      {payload.reverse().map((entry: any) => (
        <Box alignContent='center' key={entry.value} display='flex' alignItems='center' mr='m'>
          <Box
            width='xxs'
            height='xxs'
            backgroundColor={entry.color as never}
            borderRadius='50%'
            mr='xxxs'
          />
          <Typography variant='p2/light'>
            {entry.value}:{' '}
            <Typography as='span' fontWeight='500'>
              {showAvarage(entry.payload.dataKey)}
            </Typography>
            %
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

type ChartProps = {
  chartData: ChartDatum[];
  total?: number;
  averageBeginning: number;
  averageIntermediate: number;
  averageAdvanced: number;
  start?: number;
  length?: number;
};

// Module-level component so it keeps a stable identity across re-renders.
// Declaring it inside StackedBarChartComponent would create a new component
// type on every render, forcing React to unmount/remount the ResponsiveContainer
// before recharts can finish measuring it (empty chart + render loop).
const Chart: FC<ChartProps> = ({
  chartData,
  total,
  averageBeginning,
  averageIntermediate,
  averageAdvanced,
  start = 0,
  length = chartData.length,
}) => (
  <ResponsiveContainer width='100%' height={400}>
    <BarChart data={chartData.slice(start, length)} barSize={30}>
      <CartesianGrid strokeDasharray='3 3' stroke='#444' vertical={false} />
      <XAxis
        interval={0}
        minTickGap={0}
        // eslint-disable-next-line react/no-unstable-nested-components
        tick={({ x, y, payload }) => {
          const lines = String(payload.value).split('\n');
          return (
            // eslint-disable-next-line @altermaind/no-magic-numbers
            <text x={x} y={y + 10} textAnchor='middle' fill='#666'>
              {lines.map((line, index) => (
                <tspan key={line} x={x} dy={index === 0 ? 0 : 14}>
                  {line}
                </tspan>
              ))}
            </text>
          );
        }}
        dataKey='name'
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        tickLine={false}
        axisLine={false}
        domain={[0, 100]}
        tickFormatter={(value: number) => `${value}%`}
      />
      <Tooltip cursor={{ fill: 'rgba(0,0,0,0.2)' }} content={<CustomTooltip total={total} />} />
      <Legend
        verticalAlign='bottom'
        align='right'
        content={
          <CustomLegend
            averageBeginning={averageBeginning}
            averageIntermediate={averageIntermediate}
            averageAdvanced={averageAdvanced}
          />
        }
      />
      <Bar dataKey='beginning' name='Iniziale (0-2)' stackId='a' fill={colors.beginning} />
      <Bar dataKey='intermediate' name='Intermedio (3-5)' stackId='a' fill={colors.intermediate} />
      <Bar dataKey='advanced' name='Avanzato (6-7)' stackId='a' fill={colors.advanced} />
    </BarChart>
  </ResponsiveContainer>
);

export const StackedBarChartComponent: FC<Props> = ({ data, total, defaultItemsSize = 5 }) => {
  const [slide, setSlide] = useState(0);

  const chartData = useMemo<ChartDatum[]>(
    () =>
      data?.map(stat => ({
        name: stat.metric?.name || '',
        beginning: stat.beginningPercentage || 0,
        intermediate: stat.intermediatePercentage || 0,
        advanced: stat.advancedPercentage || 0,
        total: stat.total,
      })) || [],
    [data],
  );

  const { averageBeginning, averageIntermediate, averageAdvanced } = useMemo(() => {
    const length = data?.length || 1;
    return {
      averageBeginning: Math.round(
        (data?.reduce((acc, stat) => acc + (stat.beginningPercentage || 0), 0) || 0) / length,
      ),
      averageIntermediate: Math.round(
        (data?.reduce((acc, stat) => acc + (stat.intermediatePercentage || 0), 0) || 0) / length,
      ),
      averageAdvanced: Math.round(
        (data?.reduce((acc, stat) => acc + (stat.advancedPercentage || 0), 0) || 0) / length,
      ),
    };
  }, [data]);

  if (chartData.length > defaultItemsSize) {
    return (
      <Box>
        <Chart
          chartData={chartData}
          total={total}
          averageBeginning={averageBeginning}
          averageIntermediate={averageIntermediate}
          averageAdvanced={averageAdvanced}
          start={slide * defaultItemsSize}
          length={(slide + 1) * defaultItemsSize}
        />
        <NavigationForChart
          onClick={setSlide}
          data={data || []}
          DEFAULT_ITEMS_SIZE={defaultItemsSize}
          slide={slide}
        />
      </Box>
    );
  }

  return (
    <Chart
      chartData={chartData}
      total={total}
      averageBeginning={averageBeginning}
      averageIntermediate={averageIntermediate}
      averageAdvanced={averageAdvanced}
    />
  );
};
